import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';

export default function DistributionChart({
  title,
  companyValue,
  allCompanies,
  metricKey,
  metricLabel,
  maxValue = 100,
  bins = 10,
  valueFormatter = (v) => v.toFixed(1)
}) {
  const { theme } = useTheme();

  const distributionData = useMemo(() => {
    if (!allCompanies || allCompanies.length === 0) return [];

    // Get all values for this metric
    const values = allCompanies
      .map(company => {
        // Handle nested metrics
        if (metricKey.includes('.')) {
          const keys = metricKey.split('.');
          let value = company;
          for (const key of keys) {
            value = value?.[key];
          }
          return value;
        }
        return company[metricKey];
      })
      .filter(v => v != null && !isNaN(v));

    if (values.length === 0) return [];

    // Create bins
    const binSize = maxValue / bins;
    const binData = Array.from({ length: bins }, (_, i) => ({
      range: `${(i * binSize).toFixed(0)}-${((i + 1) * binSize).toFixed(0)}`,
      rangeStart: i * binSize,
      rangeEnd: (i + 1) * binSize,
      count: 0,
      isCompanyBin: false
    }));

    // Fill bins
    values.forEach(value => {
      const binIndex = Math.min(Math.floor(value / binSize), bins - 1);
      binData[binIndex].count++;
    });

    // Mark which bin contains the company
    if (companyValue != null) {
      const companyBinIndex = Math.min(Math.floor(companyValue / binSize), bins - 1);
      binData[companyBinIndex].isCompanyBin = true;
    }

    return binData;
  }, [allCompanies, metricKey, maxValue, bins, companyValue]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    return (
      <div className="bg-background-light dark:bg-background-gray border border-border dark:border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-semibold text-text-primary-light dark:text-text-primary mb-1">
          {data.range}
        </p>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary">
          {data.count} {data.count === 1 ? 'company' : 'companies'}
        </p>
        {data.isCompanyBin && (
          <p className="text-xs text-brand-primary font-semibold mt-1">
            ★ Your company
          </p>
        )}
      </div>
    );
  };

  const barColor = theme === 'dark' ? '#6366f1' : '#818cf8';
  const companyBarColor = theme === 'dark' ? '#f59e0b' : '#f59e0b';

  return (
    <div className="h-full">
      <h4 className="text-sm font-semibold text-text-primary-light dark:text-text-primary mb-3">
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={distributionData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#374151' : '#e5e7eb'} />
          <XAxis
            dataKey="range"
            tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={30}
            dy={10}  // Fine-tunes vertical distance 
          />
          <YAxis
            tick={{ fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 11 }}
            label={{
              value: 'Companies',
              angle: -90,
              position: 'insideLeft',
              style: { fill: theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 11 }
            }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: theme === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(129, 140, 248, 0.1)' }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {distributionData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isCompanyBin ? companyBarColor : barColor}
              />
            ))}
          </Bar>
          {companyValue != null && (
            <ReferenceLine
              x={distributionData.findIndex(d => d.isCompanyBin)}
              stroke={companyBarColor}
              strokeWidth={2}
              strokeDasharray="3 3"
              label={{
                value: `${valueFormatter(companyValue)}`,
                position: 'top',
                fill: companyBarColor,
                fontSize: 12,
                fontWeight: 'bold'
              }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: barColor }}></div>
          <span className="text-text-secondary-light dark:text-text-secondary">Other companies</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: companyBarColor }}></div>
          <span className="text-text-secondary-light dark:text-text-secondary">Your company</span>
        </div>
      </div>
    </div>
  );
}
